export type AnnouncementLayout = "split" | "info";

export interface AnnouncementContent {
  id: string;
  title: string;
  layout: AnnouncementLayout;
  html: string;
}

export type AnnouncementAction =
  | { action: "close" }
  | { action: "dismiss" }
  | { action: "complete" }
  | { action: "cta"; href: string | null }
  | { action: "navigate"; href: string }
  | { action: "open"; href: string };

export const ANNOUNCEMENT_MESSAGE_SOURCE = "flow-announcement";
export const ANNOUNCEMENT_MAX_HTML_BYTES = 1_500_000;

export function localDay(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function asLayout(value: string | null | undefined): AnnouncementLayout {
  return value === "info" ? "info" : "split";
}

export function isSafeAppPath(href: unknown): href is string {
  return typeof href === "string"
    && /^\/app(\/|$|\?|#)/.test(href)
    && !href.startsWith("//")
    && !/[\s\\]/.test(href);
}

export function isSafeExternalUrl(href: unknown): href is string {
  if (typeof href !== "string") return false;
  try {
    return new URL(href).protocol === "https:";
  } catch {
    return false;
  }
}

export function parseAnnouncementMessage(data: unknown): AnnouncementAction | null {
  if (!data || typeof data !== "object") return null;
  const msg = data as Record<string, unknown>;
  if (msg.source !== ANNOUNCEMENT_MESSAGE_SOURCE) return null;
  const href = typeof msg.href === "string" ? msg.href.trim() : null;
  switch (msg.action) {
    case "close":
    case "dismiss":
    case "complete":
      return { action: msg.action };
    case "cta":
      return { action: "cta", href: href && (isSafeAppPath(href) || isSafeExternalUrl(href)) ? href : null };
    case "navigate":
      return isSafeAppPath(href) ? { action: "navigate", href } : null;
    case "open":
      return isSafeExternalUrl(href) ? { action: "open", href } : null;
    default:
      return null;
  }
}

const BRIDGE_SCRIPT = `<script>(function(){
var S=${JSON.stringify(ANNOUNCEMENT_MESSAGE_SOURCE)};
function send(action,href){parent.postMessage({source:S,action:action,href:href||null},"*");}
window.FlowAnnouncement={
close:function(){send("close");},
dismiss:function(){send("dismiss");},
complete:function(){send("complete");},
cta:function(href){send("cta",href);},
navigate:function(href){send("navigate",href);},
open:function(href){send("open",href);}
};
document.addEventListener("click",function(e){
var t=e.target;if(!t||!t.closest)return;
var el=t.closest("[data-flow-action]");
if(el){e.preventDefault();send(el.getAttribute("data-flow-action"),el.getAttribute("data-flow-href")||el.getAttribute("href"));return;}
var a=t.closest("a[href]");
if(!a)return;
var h=a.getAttribute("href")||"";
if(h.charAt(0)==="#")return;
e.preventDefault();
send(h.charAt(0)==="/"?"navigate":"open",h);
},true);
document.addEventListener("keydown",function(e){if(e.key==="Escape")send("close");});
})();</script>`;

export function buildAnnouncementDocument(html: string): string {
  const closing = html.search(/<\/body\s*>/i);
  if (closing === -1) return html + BRIDGE_SCRIPT;
  return html.slice(0, closing) + BRIDGE_SCRIPT + html.slice(closing);
}

export function announcementFileName(title: string, version: number): string {
  const slug = title
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "anuncio";
  return `${slug}-v${version}.html`;
}

export function looksLikeHtmlDocument(text: string): boolean {
  return /<(html|body)[\s>]/i.test(text);
}

export type AnnouncementPhase = "draft" | "scheduled" | "active" | "ended" | "archived";

export function announcementPhase(
  a: { status: string; starts_at: string | null; ends_at: string | null },
  now: Date = new Date(),
): AnnouncementPhase {
  if (a.status === "archived") return "archived";
  if (a.status !== "published") return "draft";
  if (a.starts_at && new Date(a.starts_at) > now) return "scheduled";
  if (a.ends_at && new Date(a.ends_at) <= now) return "ended";
  return "active";
}

export const PHASE_LABEL: Record<AnnouncementPhase, string> = {
  draft: "Borrador",
  scheduled: "Programado",
  active: "Activo",
  ended: "Finalizado",
  archived: "Archivado",
};

export const LAYOUT_LABEL: Record<AnnouncementLayout, string> = {
  split: "Imagen y texto",
  info: "Solo información",
};

export function downloadHtml(fileName: string, html: string) {
  const url = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const BOGOTA_OFFSET = "-05:00";

export function dayStartIso(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  return new Date(`${day}T00:00:00${BOGOTA_OFFSET}`).toISOString();
}

export function dayEndExclusiveIso(day: string): string | null {
  const start = dayStartIso(day);
  return start ? new Date(new Date(start).getTime() + 86_400_000).toISOString() : null;
}

export function isoToBogotaDay(iso: string | null, inclusiveEnd = false): string {
  if (!iso) return "";
  const t = new Date(iso).getTime() - (inclusiveEnd ? 1 : 0);
  return new Date(t).toLocaleDateString("en-CA", { timeZone: "America/Bogota" });
}
